import React, { useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from '@/components/ui/button'
import { Input, Textarea } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select'
import DatePicker from '@/components/ui/date-input';
import { ListPurchaseInvoicesResponse } from '@/gen/purchase_invoice/list_purchase_invoices_pb'
import { PaymentAllocation, SupplierPaymentMethod } from '@/gen/purchase_invoice/common_pb'
import { toast } from '@/components/ui/use-toast'
import { pharmacyClient } from '@/connect'
import { useRouter } from '@tanstack/react-router'
import {Route} from '@/routes/(pharma)/_layout/suppliers/$supplierId/make-payment/index'
import { convertToCurrencyRounded } from '@/utils/currency_converter'
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { CheckCircle } from 'lucide-react';

const creditNoteOptions = [
    { value: 'note1', label: 'Credit Note #1' },
    { value: 'note2', label: 'Credit Note #2' },
    { value: 'note3', label: 'Credit Note #3' },
];

const DialogPayment = ({selectedInvoices, invoiceRawData, supplierID}: {
    selectedInvoices: string[],
    invoiceRawData: ListPurchaseInvoicesResponse
    supplierID: string
}) => {
    const data = Route.useLoaderData()
    // State
    const [amount, setAmount] = useState('');
    const [transactionReference, setTransactionReference] = useState('');
    const [paymentMethod, setPaymentMethod] = useState(SupplierPaymentMethod.UNSPECIFIED);
    const [notes, setNotes] = useState('');
    const [hasCreditNote, setHasCreditNote] = useState('no');
    const [selectedCreditNote, setSelectedCreditNote] = useState('');
    const [loading, setLoading] = useState(false);
    const router = useRouter();
    const [allocationState, setAllocationState] = useState<{ [invoiceId: string]: { creditNotes: { [creditNoteId: string]: number }, main: number } }>({});
    const [step, setStep] = useState(1);
    const [paymentDetails, setPaymentDetails] = useState({
        amount: '',
        paymentMethod: '',
        reference: '',
        date: undefined as Date | undefined,
        notes: '',
    });
    // Reset all state when dialog opens
    const handleDialogOpenChange = (open: boolean) => {
        if (open) {
            const total = selectedInvoices.reduce((total, invoiceId) => {
                const invoice = invoiceRawData.invoices.find(inv => inv.id === invoiceId);
                return total + (invoice ? invoice.totalAmount : 0);
            }, 0).toFixed(2);
            setAmount(total);
            setTransactionReference('');
            setPaymentMethod(SupplierPaymentMethod.UNSPECIFIED);
            setNotes('');
            setHasCreditNote('no');
            setSelectedCreditNote('');
            setAllocationState({}); // Reset allocation state
            setStep(1);
            setFinalAllocations([]);
        }
    };
    // Invoice details for accordion
    const selectedInvoiceDetails = selectedInvoices.map(invoiceId => invoiceRawData.invoices.find(inv => inv.id === invoiceId)).filter(Boolean);
    // Summary calculations
    const mainPaymentAmount = selectedInvoiceDetails.reduce((sum, inv) => sum + (inv?.totalAmount || 0), 0);
    const creditApplied = hasCreditNote === 'yes' && selectedCreditNote ? (data.creditNotes.creditNotes.find((c: any) => c.creditNoteId === selectedCreditNote)?.totalAmount || 0) : 0;
    const totalAllocated = mainPaymentAmount + creditApplied;
    const unallocatedAmount = parseFloat(amount) - totalAllocated;

    // Helper: get available credit notes for this supplier
    const availableCreditNotes = data.creditNotes?.creditNotes || [];
    // Helper: get applied so far (mocked as 0 for now)
    const getAppliedSoFar = (invoiceId: string) => 0;
    // Helper: get unallocated from payment (mocked as 15000 for now)
    const unallocatedFromPayment = 15000;
    // Helper: get max for each credit note (mocked as creditNote.totalAmount)
    const getMaxAllocationForCreditNote = (creditNoteId: string) => {
        const note = data.creditNotes?.creditNotes.find(n => n.creditNoteId === creditNoteId);
        return note ? note.totalAmount : 0;
    };

    const [finalAllocations, setFinalAllocations] = useState<PaymentAllocation[]>([]);

    return (
        <Dialog onOpenChange={handleDialogOpenChange}>
            <DialogTrigger>
                <Button size={'sm'} className='bg-blue-600 text-white'>
                    Pay Now
                </Button>
            </DialogTrigger>
            <DialogContent className="w-[90vw] h-[90vh] max-w-none max-h-none flex flex-col">
                {step === 1 && (
                    <>
                        <DialogHeader>
                            <DialogTitle>Make a Payment</DialogTitle>
                            <DialogDescription>
                                Please review the selected invoices and payment details below.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="flex-1 overflow-y-auto space-y-4 mt-2">
                            {/* Accordion for invoice details at the top */}
                            <Accordion type="multiple" className="mb-4">
                                {selectedInvoiceDetails.map((inv, idx) => (
                                    <AccordionItem value={inv!.id} key={inv!.id}>
                                        <AccordionTrigger>
                                            <div className="flex w-full justify-between items-start gap-4 px-2 py-3">
                                                <div className="flex flex-row gap-12 w-full">
                                                    <div className="flex flex-col min-w-[110px] w-full text-left">
                                                        <span className="text-xs text-gray-500">GRN</span>
                                                        <span className="text-base text-black font-medium">{inv!.grnNumber}</span>
                                                    </div>
                                                    <div className="flex flex-col min-w-[110px] w-full text-left">
                                                        <span className="text-xs text-gray-500">Invoice #</span>
                                                        <span className="text-base text-black font-medium">{inv!.invoiceNumber}</span>
                                                    </div>
                                                    <div className="flex flex-col min-w-[110px] w-full text-left">
                                                        <span className="text-xs text-gray-500">Due Date</span>
                                                        <span className="text-base text-black">{inv!.dueDate ? inv!.dueDate.toDate().toLocaleDateString() : 'N/A'}</span>
                                                    </div>
                                                    <div className="flex flex-col min-w-[110px] w-full text-left">
                                                        <span className="text-xs text-gray-500">Amount Due</span>
                                                        <span className="text-base text-black">{convertToCurrencyRounded(inv!.totalAmount)}</span>
                                                    </div>
                                                    <div className="flex flex-col min-w-[110px] w-full text-left">
                                                        <span className="text-xs text-gray-500">Status</span>
                                                        <span className="text-base"><Badge variant={
                                                            finalAllocations.filter(allocation => allocation.invoiceId === inv!.id).reduce((sum, allocation) => sum + allocation.amount, 0) >= inv!.totalAmount ? 'secondary' : 'destructive'}>{
                                                                // If the final allocation combining all the objects sum is greater than or equal to the total amount, then it is paid
                                                                finalAllocations.filter(allocation => allocation.invoiceId === inv!.id).reduce((sum, allocation) => sum + allocation.amount, 0) >= inv!.totalAmount ? 'Paid' : 'Unpaid'
                                                            }</Badge></span>
                                                    </div>
                                                </div>
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent>
                                            <div className="bg-white rounded-lg shadow p-6 mb-4 border">
                                                <div className="font-semibold mb-2">Allocate Funds to Invoice: {inv!.invoiceNumber}</div>
                                                <div className="flex flex-row gap-8 mb-4 w-full">
                                                    {/* Amount Due & Applied So Far */}
                                                    <div className="flex flex-col min-w-[180px] w-full">
                                                        <div className='flex gap-2 items-center'> 
                                                        <div>Amount Due: <span className="font-medium">{convertToCurrencyRounded(inv!.totalAmount)}</span></div>
                                                        <div>Applied So Far: <span className="font-medium">{convertToCurrencyRounded(getAppliedSoFar(inv!.id))}</span></div>

                                                        </div>
                                                        <div className="font-medium mt-6 mb-2">Apply from Credit Notes:</div>
                                                        {availableCreditNotes.length === 0 && <div className="text-gray-400 text-sm">No credit notes available</div>}
                                                        {availableCreditNotes.map((note: any) => (
                                                            <div key={note.creditNoteId} className="flex items-center justify-between gap-2 mb-2 w-full">
                                                                <div className="flex flex-col text-left">
                                                                    <span className="font-medium">{note.creditNoteNumber}</span>
                                                                    <span className="text-xs text-gray-500">{convertToCurrencyRounded(note.totalAmount)}</span>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xs text-gray-500">Max: {convertToCurrencyRounded(note.totalAmount)}</span>
                                                                    <input
                                                                        type="number"
                                                                        min={0}
                                                                        max={note.totalAmount}
                                                                        className="border rounded px-2 py-1 w-24"
                                                                        value={allocationState[inv!.id]?.creditNotes?.[note.creditNoteId] || ''}
                                                                        onChange={e => {
                                                                            const value = parseFloat(e.target.value) || 0;
                                                                            setAllocationState(prev => ({
                                                                                ...prev,
                                                                                [inv!.id]: {
                                                                                    creditNotes: {
                                                                                        ...(prev[inv!.id]?.creditNotes || {}),
                                                                                        [note.creditNoteId]: value
                                                                                    },
                                                                                    main: prev[inv!.id]?.main || 0
                                                                                }
                                                                            }))
                                                                            // Auto close the accordion if the credit note is fully allocated
                                                                            
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {/* Spacer for layout, or other content if needed */}
                                                    <div className="flex-1"></div>
                                                </div>
                                                {/* Main Payment full width below */}
                                                <div className="flex flex-col w-full mb-4">
                                                    <div className="font-medium mb-2">Apply from Main Payment:</div>
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        className="border rounded px-2 py-1 w-full"
                                                        value={allocationState[inv!.id]?.main || ''}
                                                        onChange={e => {
                                                            const value = parseFloat(e.target.value) || 0;
                                                            setAllocationState(prev => ({
                                                                ...prev,
                                                                [inv!.id]: {
                                                                    creditNotes: prev[inv!.id]?.creditNotes || {},
                                                                    main: value
                                                                }
                                                            }))
                                                        }}
                                                    />
                                                    <div className="text-xs text-gray-500 mt-1">Unallocated from payment: {convertToCurrencyRounded(
                                                        finalAllocations.filter(
                                                            allocation => allocation.invoiceId === inv!.id
                                                        ).reduce((sum, allocation) => sum + allocation.amount, 0) - inv!.totalAmount
                                                    )}</div>
                                                </div>
                                                <div className="flex justify-between items-center mt-4">
                                                    <div className="font-semibold text-lg">Total to Apply: {convertToCurrencyRounded(
                                                        Object.values(allocationState[inv!.id]?.creditNotes || {}).reduce((a, b) => a + b, 0) + (allocationState[inv!.id]?.main || 0)
                                                    )}</div>
                                                    <Button className="bg-blue-600 text-white" type="button" onClick={() => {
                                                        const totalCredit = Object.values(allocationState[inv!.id]?.creditNotes || {}).reduce((a, b) => a + b, 0);
                                                        const mainAmount = allocationState[inv!.id]?.main || 0;
                                                        if (totalCredit + mainAmount > inv!.totalAmount) {
                                                            toast({
                                                                title: 'Error',
                                                                description: 'Total allocation exceeds invoice amount',
                                                                variant: 'destructive'
                                                            });
                                                            return;
                                                        }
                                                        // One final allocation object per allocation. Each credit note is one object and the main payment is another
                                                        const finalList: PaymentAllocation[] = new Array<PaymentAllocation>();
                                                        Object.entries(allocationState[inv!.id]?.creditNotes || {}).forEach(([creditNoteId, amount]) => {
                                                            if (amount > 0) {
                                                                const allocation = new PaymentAllocation();
                                                                allocation.invoiceId = inv!.id;
                                                                allocation.creditNoteId = creditNoteId;
                                                                allocation.amount = amount;
                                                                finalList.push(allocation);
                                                            }
                                                        })

                                                        if (mainAmount > 0) {
                                                            const mainAllocation = new PaymentAllocation();
                                                            mainAllocation.invoiceId = inv!.id;
                                                            mainAllocation.amount = mainAmount;
                                                            finalList.push(mainAllocation);
                                                        }
                                                        setFinalAllocations(prev => [...prev, ...finalList]);
                                                    }}>
                                                        <span className="flex items-center gap-2">
                                                            <CheckCircle className="w-5 h-5 text-white" />
                                                            Confirm Allocation for this Invoice
                                                        </span>
                                                    </Button>
                                                </div>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </div>
                        {/* Overall Summary and Button fixed at the bottom */}
                        <div className="mt-4">
                            <hr className="border-t border-gray-200 mb-4" />
                            <div className="mb-4">
                                <div className="text-base font-semibold text-gray-800 mb-2">Overall Summary</div>
                                <div className="space-y-1 mb-4">
                                    <div className="flex justify-between text-sm text-gray-700"><span>Main Payment Amount:</span><span>{convertToCurrencyRounded(
                                        finalAllocations.filter(a => !a.creditNoteId).reduce((sum, allocation) => sum + allocation.amount, 0)
                                    )}</span></div>
                                    <div className="flex justify-between text-sm text-gray-700"><span>Credit Applied:</span><span>{convertToCurrencyRounded(
                                        finalAllocations.filter(a => a.creditNoteId).reduce((sum, allocation) => sum + allocation.amount, 0)
                                    )}</span></div>
                                    <div className="flex justify-between text-sm text-gray-700"><span>Total Allocated:</span><span>{convertToCurrencyRounded(
                                        finalAllocations.reduce((sum, allocation) => sum + allocation.amount, 0)
                                    )}</span></div>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button className='bg-blue-600 text-white' disabled={
                                    finalAllocations.length === 0 || unallocatedAmount > 0
                                } onClick={() => setStep(2)}>
                                    Review & Process Payment
                                </Button>
                            </DialogFooter>
                        </div>
                    </>
                )}
                {step === 2 && (
                    <>
                        <DialogHeader>
                            <DialogTitle>Payment Details</DialogTitle>
                        </DialogHeader>
                        <div className="flex-1 overflow-y-auto space-y-6 mt-4 px-2 w-full">
                            <div>
                                <Label htmlFor="total-amount">Total Amount</Label>
                                <Input
                                    id="total-amount"
                                    type="number"
                                    className="w-full"
                                    value={
                                        finalAllocations.reduce((sum, allocation) => sum + allocation.amount, 0).toFixed(2)
                                    }
                                    onChange={e => setPaymentDetails(pd => ({ ...pd, amount: e.target.value }))}
                                />
                                <div className="text-xs text-gray-500 mt-1">Equal to sum of selected invoices</div>
                            </div>
                            <div>
                                <Label htmlFor="payment-method">Payment Method</Label>
                                <Select value={paymentDetails.paymentMethod} onValueChange={val => setPaymentDetails(pd => ({ ...pd, paymentMethod: val }))}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select Payment Method" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="cash">Cash</SelectItem>
                                        <SelectItem value="cheque">Cheque</SelectItem>
                                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="reference">Reference Number</Label>
                                <Input
                                    id="reference"
                                    type="text"
                                    className="w-full"
                                    value={paymentDetails.reference}
                                    onChange={e => setPaymentDetails(pd => ({ ...pd, reference: e.target.value }))}
                                />
                            </div>
                            <div>
                                <Label htmlFor="notes">Additional Information</Label>
                                <Textarea
                                    id="notes"
                                    className="w-full"
                                    value={paymentDetails.notes}
                                    onChange={e => setPaymentDetails(pd => ({ ...pd, notes: e.target.value }))}
                                />
                            </div>
                        </div>
                        <DialogFooter className="flex flex-row justify-between mt-6">
                            <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                            <Button className='bg-blue-600 text-white' disabled={
                                loading || finalAllocations.length === 0 || paymentDetails.paymentMethod === '' || paymentDetails.amount === '' || parseFloat(paymentDetails.amount) <= 0 || paymentDetails.reference === ''
                            } onClick={async () => {
                                try {
                                    setLoading(true)
                                    await pharmacyClient.makePayment({
                                        supplierId: supplierID,
                                        amount: parseFloat(finalAllocations.reduce((sum, allocation) => sum + allocation.amount, 0).toFixed(2)),
                                        paymentMethod: paymentDetails.paymentMethod === "cash" ? SupplierPaymentMethod.CASH :
                                            paymentDetails.paymentMethod === "cheque" ? SupplierPaymentMethod.CHEQUE :
                                                paymentDetails.paymentMethod === "bank_transfer" ? SupplierPaymentMethod.BANK_TRANSFER :
                                                    SupplierPaymentMethod.UNSPECIFIED,
                                        paymentReference: paymentDetails.reference,
                                        notes: paymentDetails.notes,
                                        allocations: finalAllocations
                                    })
                                    toast({
                                        title: 'Payment Submitted',
                                        description: 'Your payment has been successfully submitted.',
                                        variant: 'success'
                                    });
                                    // Reset state and close dialog
                                    setAmount('');
                                    setTransactionReference('');
                                    setPaymentMethod(SupplierPaymentMethod.UNSPECIFIED);
                                    setNotes('');
                                    setHasCreditNote('no');
                                    setSelectedCreditNote('');
                                    setAllocationState({});
                                    setStep(1);
                                    setFinalAllocations([]);
                                    setLoading(false);
                                    router.navigate({
                                        to: '/suppliers/$supplierId',
                                        params: { supplierId: supplierID },
                                    })
                                } catch (error) {
                                    console.error('Payment submission error:', error);
                                    toast({
                                        title: 'Error',
                                        description: 'Failed to submit payment. Please try again.',
                                        variant: 'destructive'
                                    });
                                    setLoading(false);
                                }
                            }}>Submit Payment</Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    )
}

export default DialogPayment